import {z} from "zod";

/** Galactus schema version 1 review projection. The executable request stays on the server. */
export const mutationProposalSchema = z.object({
   schemaVersion: z.literal(1),
   proposalId: z.string().uuid(),
   capability: z.string().min(1),
   issuedAt: z.string().datetime({offset: true}),
   expiresAt: z.string().datetime({offset: true}),
   summary: z.string(),
   targetId: z.string().min(1),
   before: z.record(z.string(), z.unknown()),
   after: z.record(z.string(), z.unknown()),
   warnings: z.array(z.string()),
});
export type MutationProposal = z.infer<typeof mutationProposalSchema>;

export type GailApprovalPreview = {
   approvalId: string;
   toolCallId: string;
   proposal?: MutationProposal;
   previewUnavailable?: true;
};

/**
 * Read Wingman's `data-gailApproval` binding. Capabilities without a review projection
 * omit `proposal`; one that is present but unreadable is reported, never dropped, so the
 * approval cannot silently fall back to approving raw tool arguments.
 * A malformed binding aborts processing because its approval cannot be identified safely.
 */
export function readGailApproval(data: unknown): GailApprovalPreview {
   const binding = z.object({approvalId: z.string().min(1), toolCallId: z.string().min(1), proposal: z.unknown()}).safeParse(data);
   if (!binding.success) throw new Error("Invalid approval binding");
   const {approvalId, toolCallId, proposal} = binding.data;
   if (proposal === undefined) return {approvalId, toolCallId};
   const preview = mutationProposalSchema.safeParse(proposal);
   return preview.success ? {approvalId, toolCallId, proposal: preview.data} : {approvalId, toolCallId, previewUnavailable: true};
}

/** Object key order is not a script change; array order is meaningful. */
export function snapshotValuesEqual(left: unknown, right: unknown): boolean {
   if (Object.is(left, right)) return true;
   if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((item, index) => snapshotValuesEqual(item, right[index]));
   if (!left || !right || typeof left !== "object" || typeof right !== "object" || Array.isArray(left) || Array.isArray(right)) return false;
   const a = left as Record<string, unknown>, b = right as Record<string, unknown>;
   return Object.keys(a).length === Object.keys(b).length && Object.keys(a).every(key => Object.hasOwn(b, key) && snapshotValuesEqual(a[key], b[key]));
}

export const hasProposalChanges = (proposal: MutationProposal) => !snapshotValuesEqual(proposal.before, proposal.after);

export function scriptProposalHref(proposal: MutationProposal, conversationId?: string): string | undefined {
   if (proposal.capability !== "phone_scripts_update" || !conversationId || !z.string().uuid().safeParse(conversationId).success) return;
   if (!z.string().uuid().safeParse(proposal.targetId).success) return;
   const direction = proposal.after.direction;
   if (direction !== "inbound" && direction !== "outbound") return;
   return `/${direction}/scripts/${proposal.targetId}?${new URLSearchParams({proposalId: proposal.proposalId, conversationId})}`;
}
